const DEFAULT_NAME = "flexcharting-quotemedia-stream";

// QuoteMedia end-user sessions are non-concurrent: authenticating the same
// user again invalidates the previous SID. Hold one origin-scoped browser lock
// before requesting a SID so a second tab cannot start an authentication loop.
export class BrowserStreamLease {
  constructor({
    name = DEFAULT_NAME,
    lockManager = globalThis.navigator?.locks || null
  } = {}) {
    this.name = String(name || DEFAULT_NAME);
    this.lockManager = lockManager;
    this.held = false;
    this.acquirePromise = null;
    this.releaseHold = null;
    this.requestCompletion = null;
  }

  async acquire() {
    if (this.held) return true;
    if (!this.lockManager?.request) {
      // Authentication itself invalidates the prior non-concurrent SID, before
      // a provider connection limit can reject the second tab. Browsers that
      // cannot coordinate pre-grant access must therefore fail closed.
      return false;
    }
    if (this.acquirePromise) return this.acquirePromise;
    if (this.requestCompletion) {
      await this.requestCompletion.catch(() => {});
      this.requestCompletion = null;
    }

    let decide;
    const decision = new Promise(resolve => { decide = resolve; });
    let release;
    const hold = new Promise(resolve => { release = resolve; });
    let decided = false;
    const finishDecision = value => {
      if (decided) return;
      decided = true;
      decide(value);
    };

    try {
      this.requestCompletion = Promise.resolve(this.lockManager.request(
        this.name,
        { mode: "exclusive", ifAvailable: true },
        async lock => {
          if (!lock) {
            finishDecision(false);
            return;
          }
          this.held = true;
          this.releaseHold = release;
          finishDecision(true);
          await hold;
          this.releaseHold = null;
          this.held = false;
        }
      )).catch(() => finishDecision(false));
    } catch {
      finishDecision(false);
    }

    this.acquirePromise = decision.finally(() => { this.acquirePromise = null; });
    return this.acquirePromise;
  }

  release() {
    if (!this.lockManager?.request) {
      return;
    }
    const release = this.releaseHold;
    this.releaseHold = null;
    this.held = false;
    release?.();
  }
}
